/**
 * Loads a data source's fields, shared through the module-level field cache.
 * Returns an empty list (never throws) so a metadata failure degrades the
 * configuration form rather than crashing it.
 */

import { useEffect, useState } from 'react';
import { fetchFieldsCached } from '../services/fieldCache';
import type { DashboardDataSource, DashboardField } from '../types/dashboardTypes';

export function useSourceFields(source: DashboardDataSource | undefined): {
  fields: DashboardField[];
  loading: boolean;
} {
  const [fields, setFields] = useState<DashboardField[]>([]);
  const [loading, setLoading] = useState(false);

  /* Matches fieldCache's key, including the local case: an imported source is
     identified by its id, because it has no URL or type name of its own. */
  const key = !source
    ? ''
    : source.serviceType === 'local'
      ? `local|${source.id}`
      : `${source.serviceType}|${source.serviceUrl}|${source.layerName}|${source.arcgisLayerId ?? ''}`;

  useEffect(() => {
    if (!source) {
      setFields([]);
      return undefined;
    }

    let active = true;
    setLoading(true);

    fetchFieldsCached(source)
      .then((result) => {
        if (active) setFields(result);
      })
      .catch((error: unknown) => {
        console.error('[dashboard] field list failed', error);
        if (active) setFields([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // `key` fully describes the source identity; the object itself is recreated
    // on every render of the builder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { fields, loading };
}

/** Numeric fields only — the ones sum/avg/min/max can be applied to. */
export function numericFields(fields: DashboardField[]): DashboardField[] {
  return fields.filter((field) => field.type === 'number');
}
